import Container from 'widget/components/Container';
import Episodes from 'widget/PodcastProfile/Episodes';
import ExternalLink from 'widget/components/ExternalLink';
import IHeartLink from 'widget/components/IHeartLink';
import InfoCard from 'widget/components/InfoCard';
import MainContent from 'widget/components/MainContent';
import PLAYED_FROM from 'modules/Analytics/constants/playedFrom';
import Player from 'widget/contexts/Player';
import PlayerState, {
  PlayerErrorState,
} from 'modules/Player/constants/playerState';
import PodcastPlayer from 'widget/Podcast/PodcastPlayer';
import ShouldShow from 'components/ShouldShow';
import unescapeHTMLEntities from 'decode-html';
import useModal from 'widget/components/Modal/hooks/useModal';
import useMount from 'hooks/useMount';
import useTrackPageViewOnMount from 'widget/hooks/useTrackPageViewOnMount';
import { ComponentProps } from 'widget/PodcastProfile/types';
import { generatePodcastPageViewTrackingData } from 'widget/Podcast/helpers/analytics';
import { getErrorMessageTranslator } from 'widget/Errors/PlaybackError/helpers/getErrorMessageTranslator';
import { getInitialProps } from 'widget/PodcastProfile/helpers';
import { useContext, useEffect, useState } from 'react';
import { useTranslate } from 'widget/hooks';
import { WIDGET_DIMENSIONS } from 'constants/widgetDimensions';

function PodcastProfile({
  analytics,
  autoplay,
  description,
  episodeId,
  episodes,
  followUrl,
  imageUrl,
  origQuery,
  podcastId,
  podcastUrl,
  subtitle,
  title = '',
  urlWithQuery,
}: ComponentProps) {
  const translate = useTranslate();
  const { getCurrentlyPlaying, playerState } = useContext(Player.Context);
  const [hasPlayed, setHasPlayed] = useState(false);
  const { isError, trackId } = getCurrentlyPlaying() || { trackId: undefined };
  const [isPlaybackError, setIsPlaybackError] = useState(false);
  const pageName = 'podcast_profile';
  const playedFrom = PLAYED_FROM.RESP_WIDGET_PLAYER_PLAY;

  useTrackPageViewOnMount();

  useMount(() => {
    analytics?.trackPageView?.(
      generatePodcastPageViewTrackingData({
        name: title,
        pageName,
        podcastId,
      }),
    );
  });

  const [, toggleInfoModal] = useModal(
    <InfoCard
      body={description ? unescapeHTMLEntities(description) : ''}
      title={title}
      url={podcastUrl}
    />,
  );

  const clickEpisodes = () => {
    setHasPlayed(true);
  };

  const currentPlayingId = trackId || episodeId; // trackId will be null on initial idle state
  const currentPlayingEpisode = episodes.find(
    e => `${e.id}` === currentPlayingId,
  );
  const currentlyPlayingName =
    currentPlayingEpisode ? currentPlayingEpisode.title : '';
  const currentlyPlayingImage =
    currentPlayingEpisode ? currentPlayingEpisode.imageUrl : imageUrl;

  useEffect(() => {
    if (
      Object.values(PlayerErrorState).includes(playerState as PlayerErrorState)
    ) {
      setIsPlaybackError(true);
    } else if (playerState === PlayerState.Playing) {
      setIsPlaybackError(false);
    }
  }, [isError, playerState]);

  const podcastHasEpisodes = episodes.length > 0;

  return (
    <Container>
      <ShouldShow shouldShow={!(isError || isPlaybackError)}>
        <MainContent
          description={description ? unescapeHTMLEntities(description) : ''}
          hasPlayed={[
            PlayerState.Playing,
            PlayerState.Paused,
            PlayerState.Buffering,
          ].includes(playerState as PlayerState)}
          icons={[<IHeartLink href={podcastUrl} key="iheartlink" />]}
          infoHeading="iHeart"
          infoHeadingSmallHeight={`${subtitle} •`}
          isPodcast
          showDescription={false}
          subHeading={
            podcastHasEpisodes ?
              <ExternalLink data-test="subheading-link" href={podcastUrl}>
                {currentlyPlayingName || subtitle}
              </ExternalLink>
            : translate('This show has no episodes available yet.')
          }
          title={
            <ExternalLink data-test="title-link" href={podcastUrl}>
              {title}
            </ExternalLink>
          }
          toggleInfoModal={toggleInfoModal}
        />
      </ShouldShow>

      <PodcastPlayer
        analytics={analytics}
        analyticsData={{
          name: title,
          playedFrom,
          subName: currentlyPlayingName,
        }}
        autoplay={autoplay}
        description={description}
        dimensions={WIDGET_DIMENSIONS.PODCAST_PROFILE}
        disabled={!podcastHasEpisodes}
        episodeId={currentPlayingId}
        followUrl={followUrl}
        getPlaybackErrorMessage={getErrorMessageTranslator(translate)}
        hasPlayed={hasPlayed}
        imageUrl={currentlyPlayingImage}
        isError={isError || isPlaybackError}
        metadata={{
          episode: currentlyPlayingName,
          mainImage: imageUrl,
          podcast: title,
          trackImage: currentlyPlayingImage,
        }}
        origQuery={origQuery}
        podcastId={podcastId}
        podcastUrl={podcastUrl}
        positionBottom={false}
        shareText={translate('Share this podcast')}
        shareTitle={title}
        shareUrl={podcastUrl}
        title={title}
        urlWithQuery={urlWithQuery}
      />

      <ShouldShow
        shouldShow={episodes.length > 0 && !(isError || isPlaybackError)}
      >
        <Episodes
          analytics={analytics}
          episodes={episodes}
          onClick={clickEpisodes}
          podcastId={podcastId}
          podcastUrl={podcastUrl}
          title={title}
        />
      </ShouldShow>
    </Container>
  );
}

PodcastProfile.getInitialProps = getInitialProps;

export default PodcastProfile;
